import axios from "axios";
import { PropertyValuationData } from "../types/valuation";
import { cloudFunctionsAxios } from "./axios";
import { logger } from "../utils/logger";

// searchApi Cloud Function path (valuation, comps, sales history, demographics).
const SEARCH_API_PATH = "/searchApi";

// Normalize a single comparable, coalescing the flat and nested `propertyInfo`
// variants the valuation API returns. Input stays `any` per the existing
// convention; tightening it is Phase 4.
const transformComp = (comp: any) => ({
  id: comp.id || comp.propertyId,
  mlsSoldPrice: comp.mlsSoldPrice || comp.lastSalePrice,
  squareFeet: comp.squareFeet || comp.propertyInfo?.livingSquareFeet,
  mlsDaysOnMarket: comp.mlsDaysOnMarket,
  bedrooms: comp.bedrooms || comp.propertyInfo?.bedrooms,
  bathrooms: comp.bathrooms || comp.propertyInfo?.bathrooms,
  yearBuilt: comp.yearBuilt || comp.propertyInfo?.yearBuilt,
  estimatedValue: comp.estimatedValue,
  address: comp.address || comp.propertyInfo?.address,
  photos: comp.photos || [],
});

const transformPropertyData = async (
  apiResponse: any
): Promise<PropertyValuationData> => {
  const propertyData = apiResponse?.data?.data || apiResponse?.data;
  if (!propertyData) {
    logger.error("Invalid API response structure", { apiResponse });
    throw new Error("Invalid API response structure");
  }

  const transformedData = {
    id: propertyData.id,
    estimatedValue: propertyData.estimatedValue || 0,
    lastSalePrice: propertyData.lastSalePrice,
    propertyInfo: {
      yearBuilt: propertyData.propertyInfo?.yearBuilt,
      lotSquareFeet: propertyData.propertyInfo?.lotSquareFeet,
      livingSquareFeet: propertyData.propertyInfo?.livingSquareFeet,
      bedrooms:
        propertyData.mlsHistory?.[0]?.beds ||
        propertyData.propertyInfo?.bedrooms,
      bathrooms:
        propertyData.mlsHistory?.[0]?.baths ||
        propertyData.propertyInfo?.bathrooms,
      address: propertyData.propertyInfo?.address,
      photos: propertyData.propertyInfo?.photos || [],
    },
    propertyType: propertyData.propertyType,
    ownerOccupied: propertyData.ownerOccupied,
    floodZone: propertyData.floodZone,
    floodZoneDescription: propertyData.floodZoneDescription,
    comps: (propertyData.comps || [])
      .filter((comp: any) => comp.mlsSoldPrice || comp.estimatedValue)
      .map(transformComp),
    demographics: propertyData.demographics,
    taxInfo: propertyData.taxInfo,
  };

  return transformedData;
};

const logRequestError = (context: string, error: unknown) => {
  if (axios.isAxiosError(error)) {
    logger.error(context, {
      status: error.response?.status,
      data: error.response?.data,
      error,
    });
  } else {
    logger.error(context, { error });
  }
};

export async function searchByFullAddress(
  fullAddress: string
): Promise<PropertyValuationData> {
  try {
    const response = await cloudFunctionsAxios.post(
      `${SEARCH_API_PATH}/api/PropertyDetail`,
      {
        comps: true,
        address: fullAddress,
      }
    );

    const apiResponse = response.data.data
      ? response.data
      : { data: response.data };
    return await transformPropertyData(apiResponse);
  } catch (error) {
    logRequestError("Error fetching property details by address", error);
    throw error;
  }
}

export async function getPropertyValuation(
  listingId: string
): Promise<PropertyValuationData> {
  try {
    const response = await cloudFunctionsAxios.post(
      `${SEARCH_API_PATH}/api/PropertyDetail`,
      {
        comps: true,
        id: listingId,
      }
    );

    const apiResponse = response.data.data
      ? response.data
      : { data: response.data };
    return await transformPropertyData(apiResponse);
  } catch (error) {
    logRequestError("Error fetching property details by id", error);
    throw error;
  }
}
